import { memo, useMemo } from "react";
import { Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { getCameraKeyString } from "@src/utils/key-parser";
import { formatRoutineForDisplay } from "@src/features/alarms/utils/format-routine";
import type { CameraApiResponse } from "@src/features/cameras";
import type { AlarmRoutine } from "@src/services/api/types";

type AlarmQueryLike = {
  data?: { settings?: { routine?: AlarmRoutine | null } | null } | null;
};

export type RoutineGroup = {
  key: string;
  routine: AlarmRoutine | null;
  cameras: CameraApiResponse[];
};

export function getRoutineGroupKey(routine: AlarmRoutine | null | undefined): string {
  if (!routine) return "none";
  return formatRoutineForDisplay(routine)
    .map((w) => `${w.name}:${w.timeRanges}`)
    .join("|");
}

/**
 * Builds a cameraKey -> routine map from alarm queries aligned by index to `cameras`.
 */
export function useRoutineMap(
  cameras: CameraApiResponse[],
  alarmQueries: ReadonlyArray<AlarmQueryLike>,
): Map<string, AlarmRoutine | null> {
  return useMemo(() => {
    const map = new Map<string, AlarmRoutine | null>();
    cameras.forEach((camera, index) => {
      const cameraKey = getCameraKeyString(camera);
      const alarmData = alarmQueries[index]?.data;
      map.set(cameraKey, alarmData?.settings?.routine ?? null);
    });
    return map;
  }, [cameras, alarmQueries]);
}

export function groupCamerasByRoutine(
  cameras: CameraApiResponse[],
  routineMap: Map<string, AlarmRoutine | null>,
): RoutineGroup[] {
  const groups = new Map<string, RoutineGroup>();

  for (const camera of cameras) {
    const cameraKey = getCameraKeyString(camera);
    const routine = routineMap.get(cameraKey) ?? null;
    const key = getRoutineGroupKey(routine);

    const existing = groups.get(key);
    if (existing) {
      existing.cameras.push(camera);
    } else {
      groups.set(key, { key, routine, cameras: [camera] });
    }
  }

  return Array.from(groups.values());
}

interface RoutineGroupHeaderProps {
  routine: AlarmRoutine | null;
  allSelected: boolean;
  onSelectAll: () => void;
}

export const RoutineGroupHeader = memo(({ routine, allSelected, onSelectAll }: RoutineGroupHeaderProps) => {
  const { theme } = useUnistyles();
  const formattedWindows = routine ? formatRoutineForDisplay(routine, true) : [];

  return (
    <View flexDirection="row" justifyContent="space-between" alignItems="center">
      <View flex={1} gap="xs">
        {formattedWindows.length > 0 ?
          formattedWindows.map((window, index) => (
            <View key={index} flexDirection="row" alignItems="center" gap="xs">
              <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
              <Text textStyle="xs/regular" color="textMuted">
                {window.name}
              </Text>
              {window.timeRanges && (
                <>
                  <Text textStyle="xs/regular" color="textMuted">
                    •
                  </Text>
                  <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                  <Text textStyle="xs/regular" color="textMuted">
                    {window.timeRanges}
                  </Text>
                </>
              )}
            </View>
          ))
        : <Text textStyle="xs/regular" color="textMuted">
            No routine configured
          </Text>
        }
      </View>
      <Pressable onPress={onSelectAll} style={styles.selectAllButton}>
        <Text textStyle="xs/regular">{`${allSelected ? "Des" : "S"}elect all`}</Text>
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  selectAllButton: {
    borderWidth: 1,
    borderRadius: theme.spacing.md,
    minWidth: 90,
    height: 32,
    paddingHorizontal: theme.spacing.md,
    borderColor: theme.colors.interactive.buttons.default_background,
    alignItems: "center",
    justifyContent: "center",
  },
}));
